import { collection, deleteDoc, doc, getDoc, getDocs, limit, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';

const COLLECTION = 'subscriptionPlans';
const normalizeRole = (role) => String(role || '').toLowerCase().replace(/[\s-]+/g, '_');

async function requireSuperAdmin() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Super Admin authentication is required.');
  const snap = await getDoc(doc(db, 'users', uid));
  const data = snap.exists() ? snap.data() || {} : {};
  const role = normalizeRole(data.role || data.userRole);
  if (!['super_admin', 'superadmin'].includes(role) || data.isBlocked === true) throw new Error('Only Super Admin can manage company subscription plans.');
  return uid;
}


const finiteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const assertUniquePlanName = async (name, currentId = '') => {
  const normalized = String(name || '').trim().toLowerCase();
  if (!normalized) return;
  const snapshot = await getDocs(collection(db, COLLECTION));
  if (snapshot.docs.some((row) => row.id !== currentId && String(row.data()?.name || '').trim().toLowerCase() === normalized)) {
    throw new Error('A subscription plan with this name already exists.');
  }
};

const normalizeFeatures = (plan = {}) => {
  // When the editor supplies featuresText it is authoritative, including an
  // intentionally empty string used to clear all features.
  if (Object.prototype.hasOwnProperty.call(plan, 'featuresText')) {
    return String(plan.featuresText || '').split('\n').map((value) => value.trim()).filter(Boolean);
  }
  return Array.isArray(plan.features) ? plan.features.map((value) => String(value).trim()).filter(Boolean) : [];
};

const cleanPlan = (plan = {}) => {
  const unlimitedUsers = Boolean(plan.unlimitedUsers);
  const rawLimit = Math.floor(finiteNumber(plan.userLimit ?? plan.maxUsers ?? 1, 1));
  const currency = String(plan.currency || 'EUR').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) throw new Error('Currency must be a 3-letter code such as EUR, USD or INR.');
  return {
    name: String(plan.name || '').trim(),
    description: String(plan.description || '').trim(),
    price: Math.max(0, finiteNumber(plan.price, 0)),
    currency,
    billingCycle: String(plan.billingCycle || 'monthly'),
    userLimit: unlimitedUsers ? 0 : Math.max(1, rawLimit || 1),
    maxUsers: unlimitedUsers ? 0 : Math.max(1, rawLimit || 1),
    unlimitedUsers,
    allowExtraSeats: unlimitedUsers ? false : plan.allowExtraSeats !== false,
    isActive: plan.isActive !== false,
    isPopular: Boolean(plan.isPopular),
    sortOrder: finiteNumber(plan.sortOrder, 0),
    features: normalizeFeatures(plan),
  };
};

export async function listSubscriptionPlans() {
  await requireSuperAdmin();
  const snapshot = await getDocs(collection(db, COLLECTION));
  return snapshot.docs.map((row) => ({ id: row.id, ...row.data() }))
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0) || Number(a.price || 0) - Number(b.price || 0));
}

export async function saveSubscriptionPlan(plan) {
  const uid = await requireSuperAdmin();
  const payload = cleanPlan(plan);
  if (!payload.name) throw new Error('Plan name is required.');
  await assertUniquePlanName(payload.name, plan.id);
  const ref = plan.id ? doc(db, COLLECTION, plan.id) : doc(collection(db, COLLECTION));
  await setDoc(ref, {
    ...payload,
    createdAt: plan.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  }, { merge: true });
  return { id: ref.id, ...payload };
}

export async function deleteSubscriptionPlan(planId) {
  await requireSuperAdmin();
  const [nested, nestedPlan, direct, legacy] = await Promise.all([
    getDocs(query(collection(db, 'companies'), where('subscription.packageId', '==', planId), limit(1))),
    getDocs(query(collection(db, 'companies'), where('subscription.planId', '==', planId), limit(1))),
    getDocs(query(collection(db, 'companies'), where('packageId', '==', planId), limit(1))),
    getDocs(query(collection(db, 'companies'), where('subscriptionPlanId', '==', planId), limit(1))),
  ]);
  if (!nested.empty || !nestedPlan.empty || !direct.empty || !legacy.empty) {
    throw new Error('This plan is assigned to one or more companies. Disable it instead of deleting it, or migrate those companies first.');
  }
  await deleteDoc(doc(db, COLLECTION, planId));
  return { success: true };
}

export async function setSubscriptionPlanActive(planId, active) {
  const uid = await requireSuperAdmin();
  await setDoc(doc(db, COLLECTION, planId), { isActive: Boolean(active), updatedAt: serverTimestamp(), updatedBy: uid }, { merge: true });
  return { success: true };
}

export default { listSubscriptionPlans, saveSubscriptionPlan, deleteSubscriptionPlan, setSubscriptionPlanActive };
